import { useQuery } from "@apollo/client";
import React, { useMemo } from "react";

import {
  Alert,
  LoadingIndicator,
  SettingsPageContainer,
  useToast,
} from "../../../components";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import {
  CurrentUserFragment,
  OrganizationNotificationSettingsDocument,
  OrganizationNotificationSettingsQuery,
  UserSubscriptionSettingsV2,
} from "../../graphql";
import useUpdateCurrentUserSubscriptionsSettings from "../../graphql/hooks/useUpdateCurrentUserSubscriptionsSettings";
import {
  buildOrganizationNotificationSettingsMap,
  getOrganizationNotificationSetting,
  ORGANIZATION_NOTIFICATION_FIELDS,
} from "./notificationSettingsUtils";
import NotificationsSettingsForm, {
  FormData,
} from "./NotificationsSettingsForm";

interface NotificationsSettingsProps {
  currentUser: CurrentUserFragment;
}

const NotificationsSettings: React.FC<NotificationsSettingsProps> = ({
  currentUser,
}) => {
  const toast = useToast();
  const sendGAEvent = useSendGAEvent();
  const orgSettingsQuery = useQuery<OrganizationNotificationSettingsQuery>(
    OrganizationNotificationSettingsDocument
  );
  const organizationNotificationSettingsMap = useMemo(
    () =>
      buildOrganizationNotificationSettingsMap(
        orgSettingsQuery.data?.currentUser?.organization
          ?.organizationNotificationSettings
      ),
    [orgSettingsQuery.data]
  );
  const [updateCurrentUserSubscriptionsSettings, { error }] =
    useUpdateCurrentUserSubscriptionsSettings({
      onCompleted: (user) => {
        if (user) {
          sendGAEvent("updated", "user_notification_settings");
          toast({
            title: "Settings saved",
            status: "success",
          });
        }
      },
      onError: () => {
        toast({
          title: "Error updating user notification preferences",
          status: "error",
        });
      },
    });

  const getArrayValueFromFormData = (
    formData: FormData,
    settingKey: string
  ): UserSubscriptionSettingsV2[] => {
    let emailProp: keyof FormData = "interviewRecapSubscriptionEmail";
    let slackProp: keyof FormData = "interviewRecapSubscriptionSlack";
    let teamsProp: keyof FormData = "interviewRecapSubscriptionTeams";

    if (settingKey === "interviewPrepSubscription") {
      emailProp = "interviewPrepSubscriptionEmail";
      slackProp = "interviewPrepSubscriptionSlack";
      teamsProp = "interviewPrepSubscriptionTeams";
    }
    if (settingKey === "weeklyRecapSubscription") {
      emailProp = "weeklyRecapSubscriptionEmail";
      slackProp = "weeklyRecapSubscriptionSlack";
      teamsProp = "weeklyRecapSubscriptionTeams";
    }
    if (settingKey === "monthlyAnalyticsSubscription") {
      emailProp = "monthlyAnalyticsSubscriptionEmail";
      teamsProp = "monthlyAnalyticsSubscriptionTeams";
    }
    if (settingKey === "callShareSubscription") {
      emailProp = "callShareSubscriptionEmail";
      slackProp = "callShareSubscriptionSlack";
      teamsProp = "callShareSubscriptionTeams";
    }
    if (settingKey === "callNoteSubscription") {
      emailProp = "callNoteSubscriptionEmail";
      slackProp = "callNoteSubscriptionSlack";
      teamsProp = "callNoteSubscriptionTeams";
    }
    if (settingKey === "morningDigestSubscription") {
      emailProp = "morningDigestSubscriptionEmail";
      slackProp = "morningDigestSubscriptionSlack";
      teamsProp = "morningDigestSubscriptionTeams";
    }
    if (settingKey === "coachingSubscription") {
      emailProp = "coachingSubscriptionEmail";
      slackProp = "coachingSubscriptionSlack";
      teamsProp = "coachingSubscriptionTeams";
    }
    if (settingKey === "candidateRediscoverySubscription") {
      emailProp = "candidateRediscoverySubscriptionEmail";
      teamsProp = "candidateRediscoverySubscriptionTeams";
    }

    const subscriptions: UserSubscriptionSettingsV2[] = [];

    if (formData[emailProp]) {
      subscriptions.push(UserSubscriptionSettingsV2.Email);
    }
    if (slackProp && formData[slackProp]) {
      subscriptions.push(UserSubscriptionSettingsV2.Slack);
    }
    if (teamsProp && formData[teamsProp]) {
      subscriptions.push(UserSubscriptionSettingsV2.Teams);
    }

    return subscriptions;
  };

  const getCheckboxValueFromProperty = (
    settingValue: UserSubscriptionSettingsV2[],
    subscriptionType: UserSubscriptionSettingsV2
  ): boolean => {
    return settingValue.includes(subscriptionType);
  };

  const initialFormValues: FormData = {
    interviewRecapSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.interviewRecapSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    interviewRecapSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.interviewRecapSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    interviewRecapSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.interviewRecapSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    interviewPrepSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.interviewPrepSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    interviewPrepSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.interviewPrepSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    interviewPrepSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.interviewPrepSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    weeklyRecapSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.weeklyRecapSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    weeklyRecapSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.weeklyRecapSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    weeklyRecapSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.weeklyRecapSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    monthlyAnalyticsSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.monthlyAnalyticsSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    monthlyAnalyticsSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.monthlyAnalyticsSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    callShareSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.callShareSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    callShareSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.callShareSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    callShareSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.callShareSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    callNoteSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.callNoteSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    callNoteSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.callNoteSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    callNoteSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.callNoteSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    morningDigestSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.morningDigestSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    morningDigestSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.morningDigestSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    morningDigestSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.morningDigestSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    coachingSubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.coachingSubscription,
      UserSubscriptionSettingsV2.Email
    ),
    coachingSubscriptionSlack: getCheckboxValueFromProperty(
      currentUser.coachingSubscription,
      UserSubscriptionSettingsV2.Slack
    ),
    coachingSubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.coachingSubscription,
      UserSubscriptionSettingsV2.Teams
    ),
    candidateRediscoverySubscriptionEmail: getCheckboxValueFromProperty(
      currentUser.candidateRediscoverySubscription,
      UserSubscriptionSettingsV2.Email
    ),
    candidateRediscoverySubscriptionTeams: getCheckboxValueFromProperty(
      currentUser.candidateRediscoverySubscription,
      UserSubscriptionSettingsV2.Teams
    ),
  };

  const applyOrganizationOverrides = (formData: FormData): FormData => {
    const nextValues = { ...formData };
    (
      Object.keys(ORGANIZATION_NOTIFICATION_FIELDS) as Array<
        keyof typeof ORGANIZATION_NOTIFICATION_FIELDS
      >
    ).forEach((fieldName) => {
      const field = ORGANIZATION_NOTIFICATION_FIELDS[fieldName];
      const orgSetting = getOrganizationNotificationSetting(
        organizationNotificationSettingsMap,
        field.notificationType,
        field.channel
      );
      if (orgSetting !== "USER_CHOICE") {
        nextValues[fieldName as keyof FormData] =
          initialFormValues[fieldName as keyof FormData];
      }
    });
    return nextValues;
  };

  const onSubmit = (formData: FormData): void => {
    const nextFormData = applyOrganizationOverrides(formData);
    updateCurrentUserSubscriptionsSettings({
      variables: {
        interviewRecapSubscription: getArrayValueFromFormData(
          nextFormData,
          "interviewRecapSubscription"
        ),
        interviewPrepSubscription: getArrayValueFromFormData(
          nextFormData,
          "interviewPrepSubscription"
        ),
        weeklyRecapSubscription: getArrayValueFromFormData(
          nextFormData,
          "weeklyRecapSubscription"
        ),
        monthlyAnalyticsSubscription: getArrayValueFromFormData(
          nextFormData,
          "monthlyAnalyticsSubscription"
        ),
        callShareSubscription: getArrayValueFromFormData(
          nextFormData,
          "callShareSubscription"
        ),
        callNoteSubscription: getArrayValueFromFormData(
          nextFormData,
          "callNoteSubscription"
        ),
        morningDigestSubscription: getArrayValueFromFormData(
          nextFormData,
          "morningDigestSubscription"
        ),
        coachingSubscription: getArrayValueFromFormData(
          nextFormData,
          "coachingSubscription"
        ),
        candidateRediscoverySubscription: getArrayValueFromFormData(
          nextFormData,
          "candidateRediscoverySubscription"
        ),
      },
    });
  };

  if (!orgSettingsQuery.data?.currentUser?.organization) {
    return (
      <SettingsPageContainer
        maxW="unset"
        heading="Notification preferences"
        subHeading="View and update which notifications you receive and where they are sent."
      >
        {orgSettingsQuery.loading && <LoadingIndicator mt="25%" />}
        {orgSettingsQuery.error && (
          <Alert
            mb={6}
            status="error"
            description={orgSettingsQuery.error.message}
          />
        )}
      </SettingsPageContainer>
    );
  }

  return (
    <SettingsPageContainer
      maxW="unset"
      heading="Notification preferences"
      subHeading="View and update which notifications you receive and where they are sent."
    >
      <NotificationsSettingsForm
        interviewRecapSubscriptionEmail={
          initialFormValues.interviewRecapSubscriptionEmail
        }
        interviewRecapSubscriptionSlack={
          initialFormValues.interviewRecapSubscriptionSlack
        }
        interviewRecapSubscriptionTeams={
          initialFormValues.interviewRecapSubscriptionTeams
        }
        interviewPrepSubscriptionEmail={
          initialFormValues.interviewPrepSubscriptionEmail
        }
        interviewPrepSubscriptionSlack={
          initialFormValues.interviewPrepSubscriptionSlack
        }
        interviewPrepSubscriptionTeams={
          initialFormValues.interviewPrepSubscriptionTeams
        }
        weeklyRecapSubscriptionEmail={
          initialFormValues.weeklyRecapSubscriptionEmail
        }
        weeklyRecapSubscriptionSlack={
          initialFormValues.weeklyRecapSubscriptionSlack
        }
        weeklyRecapSubscriptionTeams={
          initialFormValues.weeklyRecapSubscriptionTeams
        }
        monthlyAnalyticsSubscriptionEmail={
          initialFormValues.monthlyAnalyticsSubscriptionEmail
        }
        monthlyAnalyticsSubscriptionTeams={
          initialFormValues.monthlyAnalyticsSubscriptionTeams
        }
        callShareSubscriptionEmail={
          initialFormValues.callShareSubscriptionEmail
        }
        callShareSubscriptionSlack={
          initialFormValues.callShareSubscriptionSlack
        }
        callShareSubscriptionTeams={
          initialFormValues.callShareSubscriptionTeams
        }
        callNoteSubscriptionEmail={initialFormValues.callNoteSubscriptionEmail}
        callNoteSubscriptionSlack={initialFormValues.callNoteSubscriptionSlack}
        callNoteSubscriptionTeams={initialFormValues.callNoteSubscriptionTeams}
        morningDigestSubscriptionEmail={
          initialFormValues.morningDigestSubscriptionEmail
        }
        morningDigestSubscriptionSlack={
          initialFormValues.morningDigestSubscriptionSlack
        }
        morningDigestSubscriptionTeams={
          initialFormValues.morningDigestSubscriptionTeams
        }
        coachingSubscriptionEmail={initialFormValues.coachingSubscriptionEmail}
        coachingSubscriptionSlack={initialFormValues.coachingSubscriptionSlack}
        coachingSubscriptionTeams={initialFormValues.coachingSubscriptionTeams}
        candidateRediscoverySubscriptionEmail={
          initialFormValues.candidateRediscoverySubscriptionEmail
        }
        candidateRediscoverySubscriptionTeams={
          initialFormValues.candidateRediscoverySubscriptionTeams
        }
        organizationNotificationSettings={organizationNotificationSettingsMap}
        error={error}
        onSubmit={onSubmit}
      />
    </SettingsPageContainer>
  );
};

export default NotificationsSettings;
